/**
 * useSvgPanZoom — drag-to-pan (at any zoom level) + scroll/button zoom for
 * a pure-SVG diagram. Apply the returned `transform` to a wrapping
 * `<g transform={transform}>` inside the `<svg>`, and spread `svgProps`
 * onto the `<svg>` element itself.
 *
 * Built for MiniEgoGraph but deliberately dependency-free (no manifest/graph
 * types) so PathDiagram or the constellation views can reuse it later.
 *
 * Usage:
 *   const panZoom = useSvgPanZoom();
 *   <svg {...panZoom.svgProps}>
 *     <g transform={panZoom.transform}>...</g>
 *   </svg>
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface SvgPanZoomOptions {
  min?: number;
  max?: number;
  step?: number;
}

export interface SvgPanZoomResult {
  transform: string;
  scalePercent: number;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
  svgProps: {
    onWheel: (e: React.WheelEvent<SVGSVGElement>) => void;
    onMouseDown: (e: React.MouseEvent<SVGSVGElement>) => void;
    style: { cursor: 'grab' | 'grabbing' };
  };
}

const DEFAULT_MIN = 1;
const DEFAULT_MAX = 3;
const DEFAULT_STEP = 0.25;
const WHEEL_STEP = 0.15;

export function useSvgPanZoom(options: SvgPanZoomOptions = {}): SvgPanZoomResult {
  const min = options.min ?? DEFAULT_MIN;
  const max = options.max ?? DEFAULT_MAX;
  const step = options.step ?? DEFAULT_STEP;

  const [scale, setScale] = useState(min);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragOrigin = useRef({ x: 0, y: 0 });
  const dragScale = useRef(scale);

  const clamp = useCallback((s: number) => Math.min(max, Math.max(min, s)), [min, max]);

  const zoomIn = useCallback(() => setScale((s) => clamp(s + step)), [clamp, step]);
  const zoomOut = useCallback(() => setScale((s) => clamp(s - step)), [clamp, step]);
  const reset = useCallback(() => {
    setScale(min);
    setTx(0);
    setTy(0);
  }, [min]);

  const onWheel = useCallback(
    (e: React.WheelEvent<SVGSVGElement>) => {
      e.preventDefault();
      setScale((s) => clamp(s + (e.deltaY < 0 ? WHEEL_STEP : -WHEEL_STEP)));
    },
    [clamp],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      dragOrigin.current = { x: e.clientX, y: e.clientY };
      dragScale.current = scale;
      setDragging(true);
    },
    [scale],
  );

  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e: MouseEvent): void => {
      const dx = (e.clientX - dragOrigin.current.x) / dragScale.current;
      const dy = (e.clientY - dragOrigin.current.y) / dragScale.current;
      dragOrigin.current = { x: e.clientX, y: e.clientY };
      setTx((prev) => prev + dx);
      setTy((prev) => prev + dy);
    };
    const handleMouseUp = (): void => setDragging(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  return {
    transform: `translate(${tx} ${ty}) scale(${scale})`,
    scalePercent: Math.round(scale * 100),
    zoomIn,
    zoomOut,
    reset,
    svgProps: {
      onWheel,
      onMouseDown,
      style: { cursor: dragging ? 'grabbing' : 'grab' },
    },
  };
}
